import axios, { AxiosError, AxiosRequestConfig } from 'axios'
import { Config } from 'config'
import { CLINIC_THEME } from 'constants/common'
import { routeName } from 'routes/routes-name'
import { getRefreshToken, getToken, isRefreshTokenExpired, isTokenExpired, logout, refreshAccessToken } from 'utils/app.utils'

export const fileUrl = Config.REACT_APP_IMAGE_URL

const instance = axios.create({
  baseURL: Config.REACT_APP_BASE_URL,
  timeout: 100000,
  headers: {
    'Content-Type': 'application/json'
  }
})

export const publicInstance = axios.create({
  baseURL: Config.REACT_APP_BASE_URL,
  timeout: 100000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor - get fresh token on every request
instance.interceptors.request.use(
  function (config: any) {
    const token = getToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  function (error) {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling and token refresh
let isRefreshing = false
let failedQueue: Array<{ resolve: (value?: any) => void; reject: (error?: any) => void }> = []

const REFRESH_TOKEN_TIMEOUT_MS = 30000

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

const refreshWithTimeout = (): Promise<string | null> => {
  return Promise.race([
    refreshAccessToken(),
    new Promise<null>((_, reject) =>
      setTimeout(() => reject(new Error('REFRESH_TOKEN_TIMEOUT')), REFRESH_TOKEN_TIMEOUT_MS)
    )
  ])
}

instance.interceptors.response.use(
  function (response) {
    return response
  },
  async function (error: AxiosError) {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }

    // Handle 401 Unauthorized - token expired
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // Check if refresh token is available and not expired
      const refreshToken = getRefreshToken()
      if (refreshToken && !isRefreshTokenExpired()) {
        if (isRefreshing) {
          // If already refreshing, queue this request with its own timeout
          return new Promise((resolve, reject) => {
            const timeoutId = setTimeout(() => {
              reject(new Error('REFRESH_QUEUE_TIMEOUT'))
            }, REFRESH_TOKEN_TIMEOUT_MS)

            failedQueue.push({
              resolve: (value?: any) => {
                clearTimeout(timeoutId)
                resolve(value)
              },
              reject: (err?: any) => {
                clearTimeout(timeoutId)
                reject(err)
              }
            })
          })
            .then(token => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`
              }
              return instance(originalRequest)
            })
            .catch(err => {
              return Promise.reject(err)
            })
        }

        originalRequest._retry = true
        isRefreshing = true

        try {
          const newAccessToken = await refreshWithTimeout()

          if (newAccessToken) {
            // Update Authorization header for the failed request
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
            }

            // Process queued requests
            processQueue(null, newAccessToken)

            // Retry the original request
            return instance(originalRequest)
          } else {
            // Refresh token failed, logout user
            processQueue(error, null)
            await logout()
            if (window.location.pathname !== '/login') {
              window.location.href = routeName.login || '/login'
            }
            return Promise.reject(error)
          }
        } catch (refreshError) {
          processQueue(refreshError, null)
          await logout()
          if (window.location.pathname !== '/login') {
            window.location.href = routeName.login || '/login'
          }
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      } else {
        // No valid refresh token, logout user
        if (isTokenExpired() || (error.response?.data as any)?.message === 'TOKEN_EXPIRED') {
          await logout()
          if (window.location.pathname !== '/login') {
            window.location.href = routeName.login || '/login'
          }
        }
      }
    }

    return Promise.reject(error)
  }
)

export default class BaseAPI {
  static api = instance
  static publicApi = publicInstance
  /** SSE helper */
  static sse<T = any>(onMessage: (data: T) => void, signal?: AbortSignal) {
    const url = `${Config.REACT_APP_BASE_URL}/connection-event?sse=true`;
    return
    // return fetchEventSource(url, {
    //   method: 'GET',
    //   headers: {
    //     Authorization: `Bearer ${token}`,
    //     Accept: 'text/event-stream',
    //   },
    //   signal,
    //   onmessage(event) {
    //     if (!event.data || event.data.startsWith(':')) return;
    //     try {
    //       const data: T = JSON.parse(event.data);
    //       onMessage(data);
    //     } catch (err) {
    //       console.error('❌ Invalid SSE message', err);
    //     }
    //   },
    //   onerror(err) {
    //     console.warn('⚠️ SSE connection error, ignoring:', err);
    //   },
    //   onopen: async (res: Response): Promise<void> => {
    //     if (!res.ok || res.headers.get('content-type') !== 'text/event-stream') {
    //       console.warn(
    //         '⚠️ SSE connection rejected:',
    //         res.status,
    //         res.statusText,
    //         'Content-Type:',
    //         res.headers.get('content-type')
    //       );
    //     }
    //     return;
    //   },
    // }).catch((err) => {
    //   console.warn('⚠️ SSE failed, ignoring:', err);
    // });
  }
}

export const lineConnectConfig = {
  LINE_NOTI_CLIENT_ID: Config.REACT_APP_LINE_NOTI_CLIENT_ID,
  LINE_NOTI_CLIENT_SECRET: Config.REACT_APP_LINE_NOTI_CLIENT_SECRET,
  LINE_NOTI_CALLBACK_URL: Config.REACT_APP_LINE_NOTI_CALLBACK_URL
}

export const clinicTheme = {
  type: CLINIC_THEME,
  theme: Config.REACT_APP_CLINIC_THEME || CLINIC_THEME.DENTAL
}